import {
  Component,
  h,
  Host,
  Listen,
  Element,
  Prop,
  Event,
  EventEmitter,
  Method,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

/**
 * @slot nav - Content of the navigation - ui-tab
 * @slot panel - Content of the panel - ui-tab-panel
 */

@Component({
  tag: 'ui-tab-group',
  styleUrl: 'ui-tab-group.scss',
  shadow: false,
})
export class UiTabGroup {
  @Element() el: HTMLElement;

  /**
   * Array of tabs that constitute the tab group
   */
  @Prop({ mutable: true }) tabs: HTMLUiTabElement[];

  /**
   * Array of panels that constitute the tab group
   */
  @Prop({ mutable: true }) panels: HTMLUiTabPanelElement[];

  /**
   * Triggered when the tag group is finished loading
   */
  @Event({ eventName: 'tabGroupIsReady' }) tabGroupIsReadyEvent: EventEmitter;

  componentWillLoad() {
    // FIXME: lazy loading disabled due to sizing issues --> see readme.md
    const images = [].slice.call(this.el.querySelectorAll('ui-image'));
    images.forEach((element) => {
      element.setAttribute('lazy-loading', 'auto');
    });

    this.tabs = [].slice.call(this.el.querySelectorAll('ui-tab'));
    this.panels = [].slice.call(this.el.querySelectorAll('ui-tab-panel'));
  }

  componentDidLoad() {
    this.addIds(this.tabs, 'tab-id');
    this.addIds(this.panels, 'tab-panel-id');
    this.deactivateAllExceptOneTab();

    this.tabGroupIsReadyEvent.emit();
  }

  addIds(array, property) {
    let index = 0;
    array.forEach((item) => {
      item.setAttribute(property, index);
      index += 1;
    });
  }

  activateTabPanel(tab: HTMLUiTabElement, panel: HTMLUiTabPanelElement) {
    tab.activate();
    panel.activate();
  }

  deactivateTabPanel(tab: HTMLUiTabElement, panel: HTMLUiTabPanelElement) {
    tab.deactivate();
    panel.deactivate();
  }

  @Method() makeNewTabActive(tabId: number) {
    this.manageTabs(tabId);
  }

  @Listen('openPanel')
  @Listen('tabClicked')
  onPanelChanged(e) {
    const targetPanelId = e.detail.tabId as number;
    this.manageTabs(targetPanelId);
  }

  manageTabs(targetPanelId: number) {
    const activeTab = this.tabs.find((element) => element.active === true);
    const activePanel = this.panels.find((element) => element.active === true);

    const tabToBeActive = this.tabs.find(
      (element) => element.tabId === targetPanelId
    );
    const panelToBeActive = this.panels.find(
      (element) => element.tabPanelId === targetPanelId
    );

    this.deactivateTabPanel(activeTab, activePanel);
    this.activateTabPanel(tabToBeActive, panelToBeActive);
  }

  deactivateAllExceptOneTab() {
    const activeTabs = this.tabs.filter((element) => element.active === true);
    const activePanels = this.panels.filter(
      (element) => element.active === true
    );
    if (activeTabs.length < 1) {
      this.tabs[0]?.activate();
      this.panels[0]?.activate();
    } else if (activeTabs.length > 1) {
      activeTabs.shift();
      activePanels.shift();
      activeTabs.forEach((tab) => tab?.deactivate());
      activePanels.forEach((panel) => panel?.deactivate());
    }
  }

  render() {
    return (
      <Host>
        <div part="nav" class="tab-group__nav">
          <slot name="nav"></slot>
        </div>
        <div part="body" class="tab-group__body">
          <slot name="panel"></slot>
        </div>
      </Host>
    );
  }
}

versionComponent(UiTabGroup);
